import { create } from "zustand";
import { persist } from "zustand/middleware";
import { loginWithCredentials, logoutRemote, refreshAccessToken } from "@/services/api/auth";
import { ApiError } from "@/services/http/errors";
import { decodeJwtExp, isJwtExpired } from "@/lib/jwt";
import { resolveProfilePicUrl } from "@/lib/media";
import { buildVoipLoginFields, getOrCreateDeviceId } from "@/lib/device";
import { getWebFcmToken } from "@/lib/firebaseMessaging";
import { ROUTES } from "@/constants/routes";
import { usePhoneStore } from "@/features/phone/store/usePhoneStore";
import { stopRingtone } from "@/features/phone/audio/ringtone";
import type { AuthProfile } from "@/types/api";

type AuthUser = {
  id?: number;
  name: string;
  email: string;
  username?: string;
  phone?: string | null;
  profilePic?: string | null;
  organizationName?: string | null;
  isSupervisor?: boolean;
  isSuperuser?: boolean;
  currentStatus?: string | null;
};

type AuthState = {
  user: AuthUser | null;
  profile: AuthProfile | null;
  accessToken: string | null;
  refreshToken: string | null;
  authExpiry: number | null;
  refreshExpiry: number | null;
  isAuthenticated: boolean;
  loginWithQr: (
    sessionToken: string,
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
  loginWithEmail: (
    email: string,
    password: string,
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
  setProfile: (profile: AuthProfile) => void;
  refreshSession: () => Promise<string | null>;
  isSessionValid: () => boolean;
  logout: (options?: { redirect?: boolean }) => void;
};

export function normalizeProfile(profile: AuthProfile): AuthProfile {
  return {
    ...profile,
    profile_pic: resolveProfilePicUrl(profile.profile_pic),
  };
}

export function profileToUser(profile: AuthProfile): AuthUser {
  const normalized = normalizeProfile(profile);
  return {
    id: normalized.id,
    name: normalized.name || normalized.username || "Agent",
    email: normalized.email,
    username: normalized.username,
    phone: normalized.ph_no ?? null,
    profilePic: normalized.profile_pic ?? null,
    organizationName: normalized.organization?.name ?? null,
    isSupervisor: Boolean(normalized.is_supervisor),
    isSuperuser: Boolean(normalized.is_superuser),
    currentStatus: normalized.current_status ?? null,
  };
}

function redirectToLogin() {
  if (typeof window === "undefined") return;
  if (window.location.pathname === ROUTES.login) return;
  window.location.assign(ROUTES.login);
}

let refreshInFlight: Promise<string | null> | null = null;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      profile: null,
      accessToken: null,
      refreshToken: null,
      authExpiry: null,
      refreshExpiry: null,
      isAuthenticated: false,

      loginWithQr: async (sessionToken) => {
        await new Promise((resolve) => setTimeout(resolve, 700));

        if (!sessionToken.trim()) {
          return { ok: false, error: "QR session expired. Refresh the code and try again." };
        }

        set({
          isAuthenticated: true,
          accessToken: null,
          refreshToken: null,
          authExpiry: null,
          refreshExpiry: null,
          profile: null,
          user: {
            name: "Agent",
            email: "agent@papaya.local",
            username: "agent",
          },
        });

        return { ok: true };
      },

      loginWithEmail: async (email, password) => {
        const trimmedEmail = email.trim();
        if (!trimmedEmail || !password.trim()) {
          return { ok: false, error: "Enter your email and password to continue." };
        }

        try {
          // Best-effort FCM token so login can persist VoIP wake token for ringtone pushes.
          const fcmToken = await getWebFcmToken().catch(() => null);
          const voip = buildVoipLoginFields(fcmToken);
          const data = await loginWithCredentials(trimmedEmail, password, voip);
          const authExpiry = decodeJwtExp(data.access);
          const refreshExpiry = decodeJwtExp(data.refresh);

          set({
            isAuthenticated: true,
            accessToken: data.access,
            refreshToken: data.refresh,
            authExpiry,
            refreshExpiry,
            profile: normalizeProfile(data.profile),
            user: profileToUser(data.profile),
          });

          return { ok: true };
        } catch (error) {
          if (error instanceof ApiError) {
            return { ok: false, error: error.userMessage };
          }
          return { ok: false, error: "Login failed. Please try again." };
        }
      },

      setProfile: (profile) => {
        const normalized = normalizeProfile(profile);
        set({
          profile: normalized,
          user: profileToUser(normalized),
          isAuthenticated: true,
        });
      },

      refreshSession: async () => {
        if (refreshInFlight) return refreshInFlight;

        refreshInFlight = (async () => {
          const { refreshToken, refreshExpiry } = get();

          if (!refreshToken || isJwtExpired(refreshExpiry, 0)) {
            get().logout({ redirect: true });
            return null;
          }

          try {
            const data = await refreshAccessToken(refreshToken);
            const authExpiry = decodeJwtExp(data.access);
            const nextRefresh = data.refresh ?? refreshToken;
            const nextRefreshExpiry = data.refresh
              ? decodeJwtExp(data.refresh)
              : refreshExpiry;

            set({
              accessToken: data.access,
              refreshToken: nextRefresh,
              authExpiry,
              refreshExpiry: nextRefreshExpiry,
              isAuthenticated: true,
            });

            return data.access;
          } catch {
            get().logout({ redirect: true });
            return null;
          } finally {
            refreshInFlight = null;
          }
        })();

        return refreshInFlight;
      },

      isSessionValid: () => {
        const { isAuthenticated, accessToken, authExpiry, refreshToken, refreshExpiry } =
          get();
        if (!isAuthenticated) return false;
        // QR demo sessions authenticate without JWTs.
        if (!accessToken && !refreshToken) return true;
        if (accessToken && !isJwtExpired(authExpiry)) return true;
        if (refreshToken && !isJwtExpired(refreshExpiry, 0)) return true;
        return false;
      },

      logout: (options) => {
        const shouldRedirect = options?.redirect ?? false;
        const { accessToken, refreshToken } = get();
        // Same request blacklists the refresh token and deletes this device's
        // FCM/VoIP row. device_id is captured before local state is cleared.
        void logoutRemote(accessToken, {
          refresh_token: refreshToken,
          device_id: getOrCreateDeviceId(),
        });
        stopRingtone();
        void usePhoneStore.getState().teardown();
        set({
          user: null,
          profile: null,
          accessToken: null,
          refreshToken: null,
          authExpiry: null,
          refreshExpiry: null,
          isAuthenticated: false,
        });
        if (shouldRedirect) redirectToLogin();
      },
    }),
    {
      name: "papaya-web-auth",
      onRehydrateStorage: () => (state) => {
        if (!state?.profile) return;
        state.profile = normalizeProfile(state.profile);
        state.user = profileToUser(state.profile);
      },
    },
  ),
);
